/**
 * Full-screen blocking gate that appears when the tenant has NOT yet confirmed
 * their default currency + locale. The user cannot proceed to any money-related
 * screen until they set + save Global Settings.
 *
 * Standing order (2026-02): "Important to have currency. There must be a toast
 * that will say, they cannot proceed unless they write their Currency. No back
 * up or fallback." — this modal is the enforcement point.
 *
 * Excludes: Global Settings itself, Login/Signup, Onboarding, Set-Password,
 * Welcome, and public routes (handled at App.js).
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertTriangle, Globe } from 'lucide-react';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

const EXEMPT_PATHS = [
  '/settings/global',
  '/smart-setup',
  '/welcome',
  '/set-password',
  '/onboarding',
];

export default function CurrencyGate() {
  const { user } = useAuth() || {};
  const { loading, ready, confirmed, error, code } = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();

  const exempt = EXEMPT_PATHS.some(p => location.pathname.startsWith(p));

  // If the tenant has ANY currency code set, we do not gate — even if the
  // legacy `currency_confirmed` field is false. This stops the recurring
  // toast on every visit reported 2026-07-22 by super_admin@konnect.
  //
  // We also cache a per-user "gate dismissed" flag in localStorage so a user
  // who has ever seen this modal for their tenant is never nagged again on
  // the same device.
  const dismissKey = user ? `konnect_currency_gate_dismissed_${user.id || user.email}` : '';
  const alreadyDismissed = dismissKey && typeof localStorage !== 'undefined'
    ? !!localStorage.getItem(dismissKey)
    : false;

  const needsGate = !loading && !exempt && user && !code && !confirmed && !alreadyDismissed;

  // Show a one-time toast (per session) when a user lands on a gated page
  // without confirming currency yet.
  useEffect(() => {
    if (!needsGate) return;
    const flagKey = `konnect_currency_toast_${user.id || user.email || 'anon'}`;
    if (sessionStorage.getItem(flagKey)) return;
    sessionStorage.setItem(flagKey, '1');
    toast.warning('Set your default currency to continue', {
      description: 'Konnect needs your currency and locale before showing financial data.',
      duration: 6000,
      action: { label: 'Set now', onClick: () => navigate('/settings/global') },
    });
  }, [needsGate, user, navigate]);

  // If the user has any currency code on record, silently mark the gate as
  // dismissed so it never re-appears (even after localStorage was cleared).
  useEffect(() => {
    if (!user || !dismissKey) return;
    if (code || confirmed) {
      try { localStorage.setItem(dismissKey, '1'); } catch { /* ignore */ }
    }
  }, [user, code, confirmed, dismissKey]);

  if (!needsGate) return null;

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="currency-gate-modal"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-6 sm:p-8 animate-fadeIn">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-text-primary">
              Confirm your currency
            </h2>
            <p className="text-sm text-gray-medium mt-1">
              To keep every invoice, quote, payroll line and report accurate, Konnect
              needs your default <strong>currency</strong>, <strong>country</strong>,
              and <strong>timezone</strong> before you can proceed.
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-3 p-2.5 bg-red-50 border border-red-100 rounded-lg text-xs text-red-700">
            {error}
          </div>
        )}

        <div className="mt-2 rounded-xl bg-gray-50 border border-gray-100 p-3 text-xs text-gray-600 space-y-1">
          <p className="flex items-center gap-1.5"><Globe className="h-3 w-3" /> Sets locale (e.g. en-IN, de-DE) for every money and date field</p>
          <p>• Applies to invoices, quotes, commissions, payroll and PDFs</p>
          <p>• You can change it later from Settings → Global Settings</p>
        </div>

        <Button
          onClick={() => navigate('/settings/global')}
          className="w-full mt-5 bg-emerald-brand hover:bg-emerald-600"
          data-testid="currency-gate-cta"
        >
          Set my currency now
        </Button>
      </div>
    </div>
  );
}
